import { useT } from "@/i18n";
import { wallMessages } from "@/i18n/messages/wall";
import { useFramesStore } from "@/stores/frames-store";
import { useWallToolIds } from "@/stores/wall-feed-store";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { WallChatsTile } from "./wall-chats-tile";
import { WallQuickNoteTile } from "./wall-quick-note-tile";
import { WallTileQueryContext } from "@/components/shapes/shared/wall-tile-query-context";
import { WallToolTile } from "./wall-tool-tile";
import { WallTileMenuProvider } from "./wall-tile-frame-renderer";
import "./wall-surface.css";

interface WallToolsRowProps {
  /** The project the chats list and the quick note save to. */
  projectId: string;
  /** The wall's trimmed search: the board, chats and memory narrow to it. */
  query: string;
  /** Everything: the chats of every project. */
  allProjects?: boolean;
  /** The wall shows in this window: the tiles run their live frames. */
  live: boolean;
  /** The plan includes the knowledge graph: the memory tile shows. */
  memory: boolean;
  /** Tiles pulsing their ring once (new work, onboarding's last scene). */
  flashingIds?: ReadonlySet<string>;
  openedId?: string | null;
  onOpen: (id: string) => void;
  onMenu: (id: string, anchor: ContextMenuAnchor) => void;
}

const NO_IDS: ReadonlySet<string> = new Set();

/**
 * Pinned (Part 09 decision 3, mockup `pinned`): a quiet label over the
 * four live tools, then the user's pins, each one column of the gallery's
 * own grid, all one height (`--wall-pinned-h`), wrapping into further rows.
 * The tools: the task board, the chats, memory (with Pro access) and
 * a quick note. Frame tiles are the real live frame, their top row a calm
 * title line; the pins follow in `wallToolsRow` order. A search narrows each
 * live tool in place (decision 2); the quick note and the pins stay.
 */
export function WallToolsRow({
  projectId,
  query,
  allProjects = false,
  live,
  memory,
  flashingIds = NO_IDS,
  openedId = null,
  onOpen,
  onMenu,
}: WallToolsRowProps) {
  const t = useT(wallMessages);
  const ids = useWallToolIds();
  // A frame never changes type, so a tile's tool is read once per render.
  const { framesById } = useFramesStore.getState();
  const typed = (type: string) =>
    ids.find((id) => framesById[id]?.frameType === type);
  const boardId = typed("taskboard");
  const memoryId = typed("memory");
  const pinIds = ids.filter((id) => id !== boardId && id !== memoryId);
  const frameTile = (id: string) => (
    <WallToolTile
      key={id}
      id={id}
      live={live}
      flashing={flashingIds.has(id)}
      opened={id === openedId}
      onOpen={onOpen}
      onMenu={onMenu}
    />
  );
  return (
    // Isolated, so tile chrome never rises over the sticky header.
    <section className="isolate mb-16" data-testid="wall-tools-row">
      {/* A quiet label on a small wall-chrome pill. */}
      <h2
        className="wall-chrome mx-1 mb-5 inline-block rounded-pill px-3 py-1 text-appkit-body font-medium tracking-[.01em] text-secondary"
        data-testid="wall-tools-label"
      >
        {t("bookmarks")}
      </h2>
      <WallTileQueryContext.Provider value={query}>
        {/* A tile's right-click opens the same menu as its pill's ⋯. */}
        <WallTileMenuProvider value={onMenu}>
          <div className="wall-pinned" data-testid="wall-pinned">
            {boardId ? frameTile(boardId) : null}
            <WallChatsTile projectId={allProjects ? null : projectId} />
            {memory && memoryId ? frameTile(memoryId) : null}
            <WallQuickNoteTile projectId={projectId} />
            {pinIds.map(frameTile)}
          </div>
        </WallTileMenuProvider>
      </WallTileQueryContext.Provider>
    </section>
  );
}
